import React, { useState, useEffect } from 'react'
import { Modal, Card, Button, Alert, Icon } from 'antd'
import { withOrderFormContext } from '../../contexts'
import PropTypes from 'prop-types'
import useFormatMoney from '../../hooks/useFormatMoney'
import useCalculateTotalAmount from '../../hooks/useCalculateTotalAmount'
import { Logic } from '../../utils/logic'
import LayoutsPreview from '../Types/PhotoBoardType/Preview'
import { getMattingPrice, shouldUseMattingTypePricing, withMattingTypePricing } from '../../hooks/withMattingTypePricing'
import CroppedImage from '../FrameSizeSelection/CroppedImage'
// import CropperModal from '../FrameSizeSelection/CropperModal'
// import { needCroppingFunc } from '../../utils/helper'

const { Meta } = Card

const CartModal = ({
  selectedShape,
  totalPrice,
  selectedAttributes,
  frameSize,
  images,
  setImages,
  showCartModal,
  setShowCartModal,
  isSubmitting,
  submitForm,
  product,
  validate,
  comment,
  signature,
  box,
  is2mmSubstrateSelected,
  error: submitError,
  layouts,
  selectedMattingType,
  isMattingStuff,
  selectedPaperType
}) => {
  // const cropperModalRef = React.useRef({ open: () => {} })
  const [error, setError] = useState({
    errorVisible: false,
    errorMessage: ''
  })

  const totalAmount = useCalculateTotalAmount()

  useEffect(() => {
    setError({ ...error, errorVisible: false })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameSize, selectedAttributes, images])

  useEffect(() => {
    if (submitError) {
      setError({
        errorVisible: true,
        errorMessage: <li key={submitError.toString()}>{submitError.toString()}</li>
      })
    }
  }, [submitError])

  const { errorVisible, errorMessage } = error

  const formatMoney = useFormatMoney()

  const _onHideCartModal = () => setShowCartModal(false)

  const _onSubmitCart = () => {
    const validateResults = validate()

    const { status, message } = validateResults

    if (!status) {
      setError({
        errorVisible: true,
        errorMessage: message.map(text => <li key={text}>{text}</li>)
      })
      return
    }

    const data = _prepareData()

    submitForm(data)
  }

  const _prepareData = () => {
    const attributeData = {}
    selectedAttributes.forEach(attribute => {
      let shouldInclude = true
      if (Object.prototype.hasOwnProperty.call(attribute, 'logic') && attribute.logic) {
        shouldInclude = (new Logic(attribute.logic)).evaluate(selectedAttributes)
      }

      // Hotfix PM-232
      if (attribute.label === 'Mat Colour') {
        shouldInclude = true
      }

      if (attribute.label === 'Substrate') {
        shouldInclude = true
      }

      // PM-359
      if (attribute.label === 'Leave Bags Open') {
        shouldInclude = true
      }

      if (attribute.label === 'Window') {
        shouldInclude = true
      }

      if (shouldInclude) attributeData[attribute.label] = attribute.value
    })

    if (signature) {
      attributeData.Signature = signature
    }

    const data = {
      product,
      price: totalPrice / totalAmount,
      quantity: totalAmount,
      options: {
        'Print Size': frameSize.title,
        Shape: selectedShape,
        ...attributeData
      }
    }

    if (layouts) {
      data.options.Layouts = JSON.stringify({
        collectionName: frameSize.name,
        layouts: layouts
      })
    }

    if (comment) data.options.Comment = comment

    return data
  }

  const _renderFrameSize = () => frameSize && (
    <div className="CartModal__Section">
      <span className="CartModal__Section__Content">Print Size: {frameSize.title}</span>
      <span className="CartModal__Section__Content">${formatMoney(frameSize.price)}</span>
    </div>
  )

  const _renderShape = () => selectedShape && (
    <div className="CartModal__Section">
      <span className="CartModal__Section__Content">Shape: {selectedShape}</span>
      <span className="CartModal__Section__Content">$0.00</span>
    </div>
  )

  const _renderBox = () => box && (
    <div className="CartModal__Section">
      <span className="CartModal__Section__Content">{box.name}</span>
      <span className="CartModal__Section__Content">${formatMoney(box.price * box.quantity)}</span>
    </div>
  )

  const _renderAttributes = () => {
    return (
      <>
        {selectedAttributes.map((selectedAttribute, index) => {
          switch (selectedAttribute.type) {
            case 'select': return _renderSelectAttribute(selectedAttribute, index)
            case 'input': return _renderInputAttribute(selectedAttribute, index)
            default: return null
          }
        })}
      </>
    )
  }

  const _renderSelectAttribute = (attribute, index) => {
    const selectedValue = attribute.value
    const selectedOption = attribute.options
      .filter(({ value }) => value === selectedValue)[0]

    let shouldDisplay = true
    if (Object.prototype.hasOwnProperty.call(attribute, 'logic') && attribute.logic) {
      shouldDisplay = (new Logic(attribute.logic)).evaluate(selectedAttributes)
    }

    if (attribute.label === 'Mat Colour') {
      shouldDisplay = true
    }

    if (attribute.label === 'Substrate') {
      shouldDisplay = true
    }

    if (attribute.label === 'Leave Bags Open') {
      shouldDisplay = true
    }

    if (attribute.label === 'Window') {
      shouldDisplay = true
    }

    let price = selectedOption?.price || 0

    if (is2mmSubstrateSelected() && selectedOption.value === '2mm - Camera Club - Hinged') {
      price = totalPrice / 1.2 * 0.2
    }

    // PM-492
    if (isMattingStuff && shouldUseMattingTypePricing(attribute)) {
      price = withMattingTypePricing(price, getMattingPrice(selectedMattingType(), selectedPaperType()), selectedMattingType())
    }

    return shouldDisplay && (
      <div className="CartModal__Section" key={index}>
        <span className="CartModal__Section__Content">{attribute.label}: {selectedOption?.value || ''}</span>
        <span className="CartModal__Section__Content">${formatMoney(price)}</span>
      </div>
    )
  }

  const _renderNewSignature = () => {
    return signature && (
      <div className="CartModal__Section" key="new-signature">
        <span className="CartModal__Section__Content">New signature uploaded</span>
        <span className="CartModal__Section__Content">$10.00</span>
      </div>
    )
  }

  const _renderInputAttribute = (attribute, index) => (
    <div className="CartModal__Section" key={index}>
      <span className="CartModal__Section__Content">{attribute.label}: {attribute.value}</span>
      <span className="CartModal__Section__Content">$0.00</span>
    </div>
  )

  const _renderImages = () => {
    // Cropping feature disabled
    // const needCroppingImages = {}
    // images.forEach((image, index) => {
    //   const { original, croppedArea } = image
    //   const croppedAreaAspect = frameSize ? (Number(frameSize.frame_height) / Number(frameSize.frame_width)) : null
    //   needCroppingImages[index] = needCroppingFunc(croppedArea, original, croppedAreaAspect)
    // })

    return (
      <div className='grid grid-cols-2 gap-3'>
        {/* Cropping warning disabled */}
        {/* {Object.values(needCroppingImages).includes(true) && (<div className='col-span-2'>
          <Alert
            showIcon
            message="Some photos don't match the print size. Please crop them to fit before placing your order."
            type="warning" />
        </div>)} */}

        {images.map((image, index) => {
          const { amount, thumbnail, original, croppedArea } = image
          const thumbnailUrl = thumbnail.getPublicUrl()
          const croppedAreaAspect = frameSize ? (Number(frameSize.frame_height) / Number(frameSize.frame_width)) : null
          const originalAreaAspect = Number(original.props.image_sizes[0]) / Number(original.props.image_sizes[1])

          return (
            <div key={index} className='col-span-1'>
              <div className='relative'>
                {/* Crop button disabled */}
                {/* <button
                  type="button"
                  className={[
                    'absolute top-1 right-1 z-50 bg-white rounded-lg p-1 bg-opacity-75 transition hover:bg-opacity-100 hover:opacity-100',
                    (croppedAreaAspect && needCroppingImages[index]) ? '' : 'opacity-30'
                  ].join(' ')}
                  onClick={() => cropperModalRef.current.open(image)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                    <path d="M24 18h-4v-14h-14v-4h-2v4h-4v2h4v14h14v4h2v-4h4v-2zm-18 0v-12h12v12h-12z" />
                  </svg>
                </button> */}
                <div>
                  <CroppedImage
                    imageSrc={thumbnailUrl}
                    croppedAreaAspect={croppedAreaAspect}
                    originalAreaAspect={originalAreaAspect}
                    croppedArea={croppedArea}/>
                </div>

                {/* Cropping check indicator disabled */}
                {/* {croppedAreaAspect && !(needCroppingImages[index]) &&
                    (<div className='absolute z-50 top-0 left-1 text-green-500 '>
                      <Icon type="check-circle" theme="filled" className='text-lg' />
                    </div>)
                } */}
              </div>
              <Meta title={`Quantity: ${amount}`}/>
            </div>
          )
        })}
      </div>
    )
  }

  const _renderLayouts = () => {
    return (
      <LayoutsPreview/>
    )
  }

  const _renderSummarize = () => {
    const finalPrice = box ? totalPrice + (box.price * box.quantity) : totalPrice
    return (
      <div className="CartModal__Section">
        <span className="CartModal__Section__Summarize">Estimated Total</span>
        <span className="CartModal__Section__Summarize">${formatMoney(finalPrice)}</span>
      </div>
    )
  }

  const _renderComment = () => {
    return (
      <div className="CartModal__Section">
        <span className="CartModal__Section__Content">Comment:</span>
        <span className="CartModal__Section__Content">{comment}</span>
      </div>
    )
  }

  const _onCloseErrorMessage = () => {
    setError({
      ...error,
      errorVisible: false
    })
  }

  const isCollection = () => {
    return selectedShape === 'COLLECTION'
  }

  /**
   * Handle image cropped completely - DISABLED
   */
  // const handleCroppedCompletely = React.useCallback((croppedArea, croppedAreaPixels, image) => {
  //   setImages(prev => {
  //     const newState = [...prev]
  //     const imageIdx = newState.findIndex(o => o.session === image.session)
  //     if (imageIdx !== -1) {
  //       newState[imageIdx] = {
  //         ...newState[imageIdx],
  //         croppedArea,
  //         croppedAreaPixels,
  //       }
  //     }
  //     return newState
  //   })
  // }, [setImages])

  // Cropping check disabled
  // const needCroppingImages = images && images.length > 0 ? images.map((image) => {
  //   const { original, croppedArea } = image
  //   const croppedAreaAspect = frameSize ? (Number(frameSize.frame_height) / Number(frameSize.frame_width)) : null
  //   return needCroppingFunc(croppedArea, original, croppedAreaAspect)
  // }) : []
  const needCroppingImages = []

  return (
    <Modal
      title="Overview your items"
      visible={showCartModal}
      onOk={_onSubmitCart}
      onCancel={_onHideCartModal}
      width={700}
      footer={[
        <Button key="back" onClick={_onHideCartModal}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isSubmitting} onClick={_onSubmitCart}>
          {isSubmitting ? 'Processing' : 'Process'}
        </Button>
      ]}
    >
      {!!errorVisible && (
        <Alert
          message={(
            <ul>
              {errorMessage}
            </ul>
          )}
          type="error"
          closable
          onClose={_onCloseErrorMessage}
        />
      )}
      <div className="CartModal flex flex-col md:flex-row">
        <div className="CartModal__ContentContainer pr-12">
          {_renderShape()}
          {_renderFrameSize()}
          {_renderAttributes()}
          {_renderNewSignature()}
          {!!comment && _renderComment()}
          {_renderBox()}
          {_renderSummarize()}
        </div>
        <div className='flex-1'>
          {isCollection() && _renderLayouts()}
          {!isCollection() && _renderImages()}
        </div>
      </div>

      {/* region cropper modal - DISABLED */}
      {/* <CropperModal ref={cropperModalRef} onCropped={handleCroppedCompletely}/> */}
      {/* endregion cropper modal */}
    </Modal>
  )
}

CartModal.propTypes = {
  selectedAttributes: PropTypes.array,
  frameSize: PropTypes.object,
  images: PropTypes.array,
  totalPrice: PropTypes.number,
  showCartModal: PropTypes.bool,
  setShowCartModal: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  product: PropTypes.string,
  validate: PropTypes.func.isRequired,
  comment: PropTypes.string,
  error: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string
  ]),
  selectedShape: PropTypes.string,
  submitForm: PropTypes.func.isRequired
}

export default withOrderFormContext(context => ({
  images: context.images,
  setImages: context.setImages,
  frameSize: context.frameSize,
  selectedShape: context.selectedShape,
  selectedAttributes: context.selectedAttributes,
  showCartModal: context.showCartModal,
  setShowCartModal: context.setShowCartModal,
  totalPrice: context.totalPrice,
  isSubmitting: context.isSubmitting,
  submitForm: context.submitForm,
  product: context.product,
  validate: context.validate,
  comment: context.comment,
  error: context.error,
  signature: context.signature,
  box: context.box,
  is2mmSubstrateSelected: context.is2mmSubstrateSelected,
  layouts: context.layouts,
  selectedMattingType: context.selectedMattingType,
  isMattingStuff: context.isMattingStuff,
  selectedPaperType: context.selectedPaperType
}))(CartModal)
